<template>
  <div class="stack-carousel-container">
    <!-- 轮播容器 -->
    <div
        class="stack-carousel"
        ref="carousel"
        @mouseenter="pauseAutoPlay"
        @mouseleave="resumeAutoPlay"
    >
      <!-- 轮播图片 -->
      <div class="slides-wrapper">
        <div
            v-for="(item, index) in items"
            :key="item.id || index"
            class="slide-item"
            :class="{
            'active': index === currentIndex,
            'prev': index === prevIndex,
            'next': index === nextIndex
          }"
            :style="getSlideStyle(index)"
            @click="handleSlideClick(item)"
        >
          <img
              :src="item.imageUrl"
              :alt="item.name || 'carousel image'"
              class="slide-image"
          />
          <!-- 图片描述（可选） -->
          <div v-if="item.name || item.description" class="slide-caption">
            <h3 v-if="item.name" class="slide-title">{{ item.name }}</h3>
            <p v-if="item.description" class="slide-desc">{{ item.description }}</p>
          </div>
        </div>
      </div>

      <!-- 导航指示器 -->
      <div v-if="showIndicators && items.length > 1" class="carousel-indicators">
        <span
            v-for="(item, index) in items"
            :key="`indicator-${item.id || index}`"
            class="indicator"
            :class="{ 'active': currentIndex === index }"
            @click="goToSlide(index)"
        />
      </div>

      <!-- 导航按钮 -->
      <button
          v-if="showArrows && items.length > 1"
          class="carousel-arrow arrow-prev"
          @click="prevSlide"
          aria-label="上一张"
      >
        <span class="arrow-icon">‹</span>
      </button>
      <button
          v-if="showArrows && items.length > 1"
          class="carousel-arrow arrow-next"
          @click="nextSlide"
          aria-label="下一张"
      >
        <span class="arrow-icon">›</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StackCarousel',
  props: {
    // 图片数据
    items: {
      type: Array,
      required: true,
      default: () => [],
      validator: (value) => {
        return value.every(item =>
            item.imageUrl && typeof item.imageUrl === 'string'
        )
      }
    },
    // 自动播放间隔（毫秒）
    interval: {
      type: Number,
      default: 3000
    },
    // 是否自动播放
    autoplay: {
      type: Boolean,
      default: true
    },
    // 显示导航箭头
    showArrows: {
      type: Boolean,
      default: true
    },
    // 显示指示器
    showIndicators: {
      type: Boolean,
      default: true
    },
    // 堆叠缩放比例
    scaleFactor: {
      type: Number,
      default: 0.85
    },
    // 堆叠位移比例
    translateFactor: {
      type: Number,
      default: 50
    },
    // 堆叠层级偏移
    zIndexOffset: {
      type: Number,
      default: 100
    }
  },

  data() {
    return {
      currentIndex: 0,
      timer: null,
      isPaused: false,
      isHovering: false
    }
  },

  computed: {
    // 计算前一张索引
    prevIndex() {
      if (this.items.length <= 1) return null
      return (this.currentIndex - 1 + this.items.length) % this.items.length
    },

    // 计算后一张索引
    nextIndex() {
      if (this.items.length <= 1) return null
      return (this.currentIndex + 1) % this.items.length
    }
  },

  mounted() {
    if (this.autoplay && this.items.length > 1) {
      this.startAutoPlay()
    }
  },

  beforeDestroy() {
    this.clearTimer()
  },

  methods: {
    // 开始自动播放
    startAutoPlay() {
      if (!this.autoplay || this.items.length <= 1 || this.isPaused) return

      this.clearTimer()
      this.timer = setInterval(() => {
        this.nextSlide()
      }, this.interval)
    },

    // 暂停自动播放
    pauseAutoPlay() {
      this.isPaused = true
      this.clearTimer()
    },

    // 恢复自动播放
    resumeAutoPlay() {
      this.isPaused = false
      if (this.autoplay && this.items.length > 1) {
        this.startAutoPlay()
      }
    },

    // 清除定时器
    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    // 下一张
    nextSlide() {
      if (this.items.length <= 1) return
      this.currentIndex = (this.currentIndex + 1) % this.items.length
    },

    // 上一张
    prevSlide() {
      if (this.items.length <= 1) return
      this.currentIndex = (this.currentIndex - 1 + this.items.length) % this.items.length
    },

    // 跳转到指定幻灯片
    goToSlide(index) {
      if (this.items.length <= 1) return
      if (index >= 0 && index < this.items.length) {
        this.currentIndex = index
      }
    },

    // 计算幻灯片样式
    getSlideStyle(index) {
      const total = this.items.length
      if (total <= 1) {
        return {
          transform: 'translateX(0) scale(1)',
          zIndex: this.zIndexOffset,
          opacity: 1
        }
      }

      // 计算当前索引与目标索引的差值
      let diff = index - this.currentIndex

      // 处理循环
      if (diff > Math.floor(total / 2)) {
        diff -= total
      } else if (diff < -Math.floor(total / 2)) {
        diff += total
      }

      // 计算缩放
      const scale = Math.pow(this.scaleFactor, Math.abs(diff))

      // 计算位移
      const translateX = diff * this.translateFactor

      // 计算层级：距离当前索引越远，层级越低
      const zIndex = this.zIndexOffset - Math.abs(diff) * 10

      // 计算透明度
      const opacity = Math.max(0.3, 1 - Math.abs(diff) * 0.3)

      return {
        transform: `translateX(${translateX}%) scale(${scale})`,
        zIndex: zIndex,
        opacity: opacity
      }
    },

    // 幻灯片点击事件
    handleSlideClick(item) {
      const index = this.items.findIndex(i => i.id === item.id || i.imageUrl === item.imageUrl)
      if (index !== -1 && index !== this.currentIndex) {
        this.goToSlide(index)
      }
      this.$emit('slide-click', item)
    }
  },

  watch: {
    items: {
      handler() {
        this.currentIndex = 0
        if (this.autoplay) {
          this.resumeAutoPlay()
        }
      },
      deep: true
    },
    autoplay(newVal) {
      if (newVal && !this.isPaused) {
        this.startAutoPlay()
      } else {
        this.clearTimer()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.stack-carousel-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.stack-carousel {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 600px;
}

.slides-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  //align-items: center;
  justify-content: center;
}

.slide-item {
  position: absolute;
  width: 80%;
  max-width: 400px;
  //height: 70%;
  //max-height: 800px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;

  &.active {
    cursor: default;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  }

  //&:not(.active):hover {
  //  transform: translateX(var(--translateX, 0)) scale(var(--scale, 0.85)) !important;
  //  opacity: 0.8 !important;
  //  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.25);
  //}
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  .slide-item.active:hover & {
    transform: scale(1.02);
  }
}

.slide-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  color: white;
  padding: 20px;
  transform: translateY(100%);
  transition: transform 0.3s ease;

  .slide-item.active & {
    transform: translateY(0);
  }
}

.slide-title {
  margin: 0 0 8px 0;
  font-size: 1.2em;
  font-weight: bold;
}

.slide-desc {
  margin: 0;
  font-size: 0.9em;
  opacity: 0.9;
}

.carousel-indicators {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
  z-index: 1000;
}

.indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.8);
    transform: scale(1.2);
  }

  &.active {
    background-color: white;
    transform: scale(1.2);
  }
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #333;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: white;
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &.arrow-prev {
    left: 20px;
  }

  &.arrow-next {
    right: 20px;
  }
}

.arrow-icon {
  line-height: 1;
  font-weight: bold;
}

@media (max-width: 768px) {
  .stack-carousel {
    min-height: 400px;
  }
}
</style>
